
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { debugLogger } from '@/utils/debugLogger';
import { appSettingsCache } from '@/lib/appSettingsCache';

interface AppSetting {
  id: string;
  key: string;
  value: any;
  type: 'boolean' | 'timestamp' | 'string' | 'number';
  description: string;
  created_at: string;
  updated_at: string;
}

interface AppSettings {
  launch_date: Date | null;
  maintenance_mode: boolean;
  last_commit_sha: string;
}

export const useAppSettings = () => {
  const [settings, setSettings] = useState<AppSettings>({
    launch_date: null,
    maintenance_mode: false,
    last_commit_sha: 'local-dev'
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchSettings = async () => {
    try {
      const cachedSettings = await appSettingsCache.getSettings();
      setSettings(cachedSettings);
      setLoading(false);
      debugLogger.debug('useAppSettings: Settings loaded from cache');
    } catch (err: any) {
      debugLogger.error('useAppSettings: Error fetching settings', err);
      setError(err.message);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();

    // Subscribe to singleton updates
    const unsubscribe = appSettingsCache.subscribe((newSettings) => {
      setSettings(newSettings);
      debugLogger.debug('useAppSettings: Settings updated from singleton');
    });

    // Subscribe to realtime changes and invalidate cache
    const channel = supabase
      .channel('app_settings_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'app_settings'
        },
        () => {
          debugLogger.debug('useAppSettings: Realtime update detected');
          appSettingsCache.invalidate();
          fetchSettings();
        }
      )
      .subscribe();

    return () => {
      unsubscribe();
      supabase.removeChannel(channel);
    };
  }, []);

  // Helper functions
  const isAfterLaunchDate = (): boolean => {
    if (!settings.launch_date) return true;
    return new Date() >= settings.launch_date;
  };

  const isMaintenanceMode = (): boolean => {
    return settings.maintenance_mode;
  };

  const updateSetting = async (key: string, value: any, type: string) => {
    try {
      const { error } = await supabase
        .from('app_settings')
        .update({ value: value, type })
        .eq('key', key);

      if (error) throw error;
      
      debugLogger.debug('useAppSettings: Setting updated', { key, value });
    } catch (err: any) {
      debugLogger.error('useAppSettings: Error updating setting', err);
      throw err;
    }
  };

  const getAppVersion = (): string => {
    return settings.last_commit_sha || 'local-dev';
  };

  return {
    settings,
    loading,
    error,
    isAfterLaunchDate,
    isMaintenanceMode,
    updateSetting,
    refetch: fetchSettings,
    getAppVersion
  };
};
